import { MantleUnstakeRequestsManagerAbi } from '../abis/mantle'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/**
 * Mantle mETH — log-free via caller-supplied IDs.
 *
 * No user-keyed on-chain storage exists; the only on-chain
 * enumeration path is the `UnstakeRequestCreated` event. Per the
 * no-logs constraint, the reader expects the caller (frontend /
 * worker-api) to have captured `unstakeRequestId` from the withdraw
 * tx receipt at request time and to pass the accumulated list as
 * `knownIds`.
 *
 * Returns `[]` when no IDs are supplied — no enumeration is attempted.
 *
 * Per id: multicall `requestByID(id)` + `requestInfo(id)`. The latter
 * already returns the claimable flag and the settled amount.
 */
export const readerMantle: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry, knownIds) => {
    if (!entry.withdrawalContract) return []
    if (!knownIds || knownIds.length === 0) return []

    // Mantle uses plain bigint IDs — drop any composite ids.
    const ids = knownIds.filter((v): v is bigint => typeof v === 'bigint')
    if (ids.length === 0) return []

    const calls = ids.flatMap((id) => [
      {
        address: entry.withdrawalContract!,
        name: 'requestByID',
        params: [id],
      },
      {
        address: entry.withdrawalContract!,
        name: 'requestInfo',
        params: [id],
      },
    ])
    const results = (await multicallRetry({
      chain: chainId,
      calls: calls as unknown as never[],
      abi: calls.map(() => MantleUnstakeRequestsManagerAbi) as unknown as never,
    })) as unknown[]

    const out: LstWithdrawalRequest[] = []
    const lcUser = user.toLowerCase()
    for (let i = 0; i < ids.length; i++) {
      const detail = results[i * 2] as
        | { requester?: unknown; ethRequested?: unknown }
        | unknown[]
        | undefined
      const info = results[i * 2 + 1] as
        | { isFinalized?: unknown; claimableAmount?: unknown }
        | unknown[]
        | undefined
      if (!detail || !info) continue
      const requester = Array.isArray(detail)
        ? (detail[1] as string | undefined)
        : (detail.requester as string | undefined)
      // Reject IDs that don't belong to the caller — defends against
      // stale localStorage shared across accounts.
      if (typeof requester === 'string' && requester.toLowerCase() !== lcUser) {
        continue
      }
      const ethRequested = Array.isArray(detail)
        ? toBigInt(detail[3])
        : toBigInt(detail.ethRequested)
      const isFinalized = Array.isArray(info)
        ? Boolean(info[0])
        : Boolean(info.isFinalized)
      const claimable = Array.isArray(info)
        ? toBigInt(info[1])
        : toBigInt(info.claimableAmount)
      if (ethRequested === undefined) continue
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: ids[i].toString(),
        amountUnderlying: (claimable ?? ethRequested).toString(),
        status: isFinalized ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
